<template>
  <div class="product-quality-notes grid critical-component-clear">
    <div
      class="product-quality-notes__container col xs-span l6-59"
    >
      <h2
        class="product-quality-notes__header font-family-freight"
        v-text="heading"
      />
      <ul
        v-if="!isMobile"
        class="product-quality-notes__items"
      >
        <li
          v-for="(item, index) of qualityNoteItems"
          :key="`qualityNote${index}`"
          class="product-quality-notes__item"
          :class="threeNotesClass"
        >
          <video-player
            v-if="item.media?.mediaType === 'video'"
            :id="item.media.id"
            :controls="{
              hide: true,
              muteButton: item.videoSettings.videoAudioControls.value,
              id: `${liquid.sectionId}-${item.media.id}`,
            }"
            :intersection-observer="{ enable: item.videoSettings.videoAutoPlay }"
            :is-dark="item.videoSettings.isDark === 'true'"
            ratio="398:501"
            :sources="item.media.sources"
            preload
          />

          <responsive-image
            v-else
            :image="item.media?.image"
            ratio="398:501"
          />
          <div
            class="product-quality-notes__title font-family-freight"
            v-text="item.title"
          />
          <div
            class="product-quality-notes__copy"
            v-html="item.copy"
          />
        </li>
      </ul>
      <splide
        v-else
        ref="carousel"
        class="product-quality-notes__carousel"
        :has-track="false"
        :options="localConfig"
      >
        <splide-track>
          <splide-slide
            v-for="(item, index) of qualityNoteItems"
            :key="index"
          >
            <video-player
              v-if="item.media?.mediaType === 'video'"
              :id="item.media.id"
              :controls="{
                hide: true,
                muteButton: item.videoSettings.videoAudioControls.value,
                id: `${liquid.sectionId}-${item.media.id}`,
              }"
              :intersection-observer="{ enable: item.videoSettings.videoAutoPlay }"
              :is-dark="item.videoSettings.isDark === 'true'"
              ratio="398:501"
              :sources="item.media.sources"
              preload
            />
            <responsive-image
              v-else
              :image="item.media?.image"
              ratio="312:393"
            />
            <div
              class="product-quality-notes__title font-family-freight"
              v-text="item.title"
            />
            <div
              class="product-quality-notes__copy"
              v-html="item.copy"
            />
          </splide-slide>
        </splide-track>
      </splide>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product quality notes (product-quality-notes)
 * -----------------------------------------------------------------------------
 * Product Quality and Fit Section.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */

import { mapState } from 'vuex'
import ResponsiveImage from '~global/images/responsive-image'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import VideoPlayer from '~async/video-player/video-player'

export default {
  name: 'ProductQualityNotes',
  components: {
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer,
  },
  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    heading: {
      type: String,
      default: '',
    },
    qualityNoteItems: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localConfig: {},
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Computes whether to use the class for 3 notes.
     * @returns {String}
     */
    threeNotesClass() {
      return this.qualityNoteItems.length < 4 ? 'product-quality-notes__three-items' : ''
    },
  },

  watch: {
    isMobile: {
      async handler(newIsMobile) {
        if (!newIsMobile) {
          // If desktop, reset to position 0 in case user goes back to mobile
          this.localConfig = {
            arrows: false,
            direction: 'ltr',
            drag: true,
            pagination: false,
            perPage: 1,
            start: 0,
            type: 'slide',
            gap: 'var(--spacing-xs)',
            padding: { left: '1rem', right: '4rem' },
          }
          return
        }

        // If mobile, wait for splide to be mounted, then hook in
        await this.$nextTick()
        this.$refs.carousel?.splide.event.on('move', (newIndex) => {
          let left = '2.5rem'
          let right = '2.5rem'

          if (newIndex === 0) {
            left = '1rem'
            right = '4rem'
          }

          if (newIndex === this.qualityNoteItems.length - 1) {
            left = '4rem'
            right = '1rem'
          }

          this.localConfig = {
            arrows: false,
            direction: 'ltr',
            drag: true,
            pagination: false,
            perPage: 1,
            start: 0,
            type: 'slide',
            gap: 'var(--spacing-xs)',
            padding: {
              left,
              right,
            },
          }
        })
      },
      immediate: true,
    },
  },

  beforeMount() {
    this.localConfig = {
      arrows: false,
      direction: 'ltr',
      drag: true,
      pagination: false,
      perPage: 1,
      start: 0,
      type: 'slide',
      gap: 'var(--spacing-xs)',
      padding: { left: '1rem', right: '4rem' },
      lazyload: 'sequential',
    }
  },
}
</script>

<style lang="scss">
@import './product-quality-notes';
</style>
